import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Savings } from '@mui/icons-material';
import Swal from 'sweetalert2';
import paymentportalservice from '../../../services/paymentportalservice';

const todayDateValue = () => new Date().toISOString().split('T')[0];

// Locked date is stored as an ISO Date on the backend but edited here as a
// plain "YYYY-MM-DD" string (native date input) — this pulls just the date
// portion back out regardless of which shape the campaign prop arrives in.
const toDateInputValue = (value) => {
    if (!value) return '';
    const iso = typeof value === 'string' ? value : new Date(value).toISOString();
    return iso.split('T')[0];
};

const AddRevenueDialog = React.memo(function AddRevenueDialog({ open, campaign, onClose, onSaved }) {
    const [amount, setAmount] = useState('');
    const [lockedDate, setLockedDate] = useState('');
    const [saving, setSaving] = useState(false);

    // Re-seed from the campaign each time the dialog is (re)opened, so
    // re-opening it for a different campaign — or the same one after its
    // revenue changed — doesn't show a stale amount/date left over from the
    // previous open.
    useEffect(() => {
        if (!open || !campaign) return;
        setAmount(campaign.revenueAmount != null ? String(campaign.revenueAmount) : '');
        setLockedDate(toDateInputValue(campaign.revenueLockedDate) || todayDateValue());
    }, [open, campaign]);

    const amountNum = Number(amount);
    const isAmountValid = amount !== '' && Number.isFinite(amountNum) && amountNum >= 0;
    const isValid = isAmountValid && Boolean(lockedDate);

    const netRevenue = useMemo(() => {
        if (!isAmountValid || !campaign) return null;
        return amountNum - (Number(campaign.totalPayment) || 0);
    }, [isAmountValid, amountNum, campaign]);

    const handleSave = async () => {
        if (!isValid || !campaign) return;
        const campaignId = campaign._id || campaign.id;
        setSaving(true);
        try {
            const updated = await paymentportalservice.AddCampaignRevenue({
                campaignId,
                revenueAmount: amountNum,
                revenueLockedDate: lockedDate,
            });
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Revenue Saved!',
                showConfirmButton: false,
                timer: 1200,
            });
            onSaved?.(updated);
            onClose?.();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Could not save revenue',
                text: error?.response?.data || error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Savings sx={{ color: '#7C3AED' }} />
                    <Typography variant="h6">Add Revenue</Typography>
                </Stack>
                {campaign?.name && (
                    <Typography variant="body2" color="text.secondary">
                        {campaign.name}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                    <TextField
                        label="Revenue Amount (₹)"
                        type="number"
                        fullWidth
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        error={amount !== '' && !isAmountValid}
                        helperText={amount !== '' && !isAmountValid ? 'Enter a valid non-negative amount' : ' '}
                        inputProps={{ min: 0 }}
                    />
                    <TextField
                        label="Locked Date"
                        type="date"
                        fullWidth
                        value={lockedDate}
                        onChange={(e) => setLockedDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    {netRevenue != null && (
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color={netRevenue >= 0 ? 'success.main' : 'error.main'}
                        >
                            Net Revenue: ₹{netRevenue.toLocaleString('en-IN')}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#ee4c3a' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isValid || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default AddRevenueDialog;
